import {
  Account,
  AtAGlanceReport,
  Budget,
  Category,
  DailyReportEntry,
  Debt,
  FinanceFolder,
  Habit,
  HabitLog,
  MonthlyPlan,
  Note,
  RecurringBill,
  ResourceLink,
  SavingsGoal,
  Task,
  Transaction,
  User,
  YearlyGoal,
} from '../types';

export const STORAGE_KEYS = {
  USER: 'mylife_user',
  FINANCE_FOLDERS: 'mylife_finance_folders',
  ACCOUNTS: 'mylife_accounts',
  CATEGORIES: 'mylife_categories',
  TRANSACTIONS: 'mylife_transactions',
  BUDGETS: 'mylife_budgets',
  DEBTS: 'mylife_debts',
  NOTES: 'mylife_notes',
  TASKS: 'mylife_tasks',
  HABITS: 'mylife_habits',
  HABIT_LOGS: 'mylife_habit_logs',
  PERSONAL_REPORTS: 'mylife_personal_reports',
  MONTHLY_PLANS: 'mylife_monthly_plans',
  AT_A_GLANCE_REPORTS: 'mylife_at_a_glance_reports',
  SAVINGS_GOALS: 'mylife_savings_goals',
  RECURRING_BILLS: 'mylife_recurring_bills',
  YEARLY_GOALS: 'mylife_yearly_goals',
  RESOURCES: 'mylife_resources',
  API_URL: 'mylife_api_url',
};

export const DEFAULT_USER: User = {
  id: 'user_default',
  name: 'User',
  email: '',
  currency: 'BDT',
  language: 'en',
  theme: 'dark',
};

export const DEFAULT_FINANCE_FOLDERS: FinanceFolder[] = [
  {
    id: 'f_personal',
    name: 'Personal Finance',
    type: 'personal',
    description: 'Personal income, household expenses & savings',
    color: '#10b981',
    icon: 'User',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'f_business_1',
    name: 'Business',
    type: 'business',
    description: 'Business revenue, operations & expenses',
    color: '#3b82f6',
    icon: 'Briefcase',
    createdAt: new Date().toISOString(),
  },
  {
    id: 'f_org_1',
    name: 'Organization',
    type: 'organization',
    description: 'Organization & association ledger',
    color: '#8b5cf6',
    icon: 'Building2',
    createdAt: new Date().toISOString(),
  },
];

export const DEFAULT_ACCOUNTS: Account[] = [
  {
    id: 'acc_cash',
    name: 'Cash Wallet',
    type: 'cash',
    balance: 0,
    color: '#10b981',
    icon: 'Banknote',
  },
  {
    id: 'acc_bank',
    name: 'Bank Account',
    type: 'bank',
    balance: 0,
    color: '#3b82f6',
    icon: 'Landmark',
  },
  {
    id: 'acc_bkash',
    name: 'bKash / Mobile Banking',
    type: 'mobile_banking',
    balance: 0,
    color: '#ec4899',
    icon: 'Smartphone',
  },
];

export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'cat_food', name: 'Food & Dining', type: 'expense', icon: 'Utensils', color: '#f97316' },
  { id: 'cat_transport', name: 'Transport & Fuel', type: 'expense', icon: 'Car', color: '#06b6d4' },
  { id: 'cat_rent', name: 'House Rent', type: 'expense', icon: 'Home', color: '#8b5cf6' },
  { id: 'cat_bills', name: 'Utilities & Bills', type: 'expense', icon: 'Zap', color: '#eab308' },
  { id: 'cat_shopping', name: 'Shopping & Groceries', type: 'expense', icon: 'ShoppingBag', color: '#ec4899' },
  { id: 'cat_health', name: 'Health & Medical', type: 'expense', icon: 'HeartPulse', color: '#ef4444' },
  { id: 'cat_other', name: 'Other Expenses', type: 'expense', icon: 'Tag', color: '#64748b' },
  { id: 'cat_salary', name: 'Monthly Salary', type: 'income', icon: 'Briefcase', color: '#10b981' },
  { id: 'cat_freelance', name: 'Business / Projects', type: 'income', icon: 'Laptop', color: '#3b82f6' },
];

export const DEFAULT_TRANSACTIONS: Transaction[] = [];
export const DEFAULT_BUDGETS: Budget[] = [];
export const DEFAULT_DEBTS: Debt[] = [];
export const DEFAULT_NOTES: Note[] = [];
export const DEFAULT_TASKS: Task[] = [];
export const DEFAULT_HABITS: Habit[] = [];
export const DEFAULT_HABIT_LOGS: HabitLog[] = [];
export const DEFAULT_PERSONAL_REPORTS: DailyReportEntry[] = [];
export const DEFAULT_SAVINGS_GOALS: SavingsGoal[] = [];
export const DEFAULT_RECURRING_BILLS: RecurringBill[] = [];
export const DEFAULT_YEARLY_GOALS: YearlyGoal[] = [];
export const DEFAULT_RESOURCES: ResourceLink[] = [];

export type StorageChangeListener = (key: string, value: any) => void;

export class StorageService {
  private static listeners: Set<StorageChangeListener> = new Set();

  static onStorageChange(listener: StorageChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  static getItem<T>(key: string, defaultValue: T): T {
    try {
      let item = localStorage.getItem(key);
      if (item === null && key.startsWith('mylife_')) {
        const legacyKey = key.replace(/^mylife_/, 'mytime_');
        item = localStorage.getItem(legacyKey);
      }
      if (item === null) return defaultValue;
      return JSON.parse(item);
    } catch (e) {
      console.warn(`Error reading localStorage key "${key}":`, e);
      return defaultValue;
    }
  }

  static setItem<T>(key: string, value: T, silent = false): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      if (!silent) {
        localStorage.setItem('mylife_last_local_edit_timestamp', Date.now().toString());
        this.listeners.forEach((fn) => {
          try {
            fn(key, value);
          } catch (err) {
            console.error('Storage listener error:', err);
          }
        });
      }
    } catch (e) {
      console.error(`Error saving localStorage key "${key}":`, e);
    }
  }

  static removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Error removing localStorage key "${key}":`, e);
    }
  }

  static loadAll() {
    return {
      user: this.getItem<User>(STORAGE_KEYS.USER, DEFAULT_USER),
      financeFolders: this.getItem<FinanceFolder[]>(STORAGE_KEYS.FINANCE_FOLDERS, DEFAULT_FINANCE_FOLDERS),
      accounts: this.getItem<Account[]>(STORAGE_KEYS.ACCOUNTS, DEFAULT_ACCOUNTS),
      categories: this.getItem<Category[]>(STORAGE_KEYS.CATEGORIES, DEFAULT_CATEGORIES),
      transactions: this.getItem<Transaction[]>(STORAGE_KEYS.TRANSACTIONS, DEFAULT_TRANSACTIONS),
      budgets: this.getItem<Budget[]>(STORAGE_KEYS.BUDGETS, DEFAULT_BUDGETS),
      debts: this.getItem<Debt[]>(STORAGE_KEYS.DEBTS, DEFAULT_DEBTS),
      notes: this.getItem<Note[]>(STORAGE_KEYS.NOTES, DEFAULT_NOTES),
      tasks: this.getItem<Task[]>(STORAGE_KEYS.TASKS, DEFAULT_TASKS),
      habits: this.getItem<Habit[]>(STORAGE_KEYS.HABITS, DEFAULT_HABITS),
      habitLogs: this.getItem<HabitLog[]>(STORAGE_KEYS.HABIT_LOGS, DEFAULT_HABIT_LOGS),
      personalReports: this.getItem<DailyReportEntry[]>(STORAGE_KEYS.PERSONAL_REPORTS, DEFAULT_PERSONAL_REPORTS),
      monthlyPlans: this.getItem<Record<string, MonthlyPlan>>(STORAGE_KEYS.MONTHLY_PLANS, {}),
      atAGlanceReports: this.getItem<Record<string, AtAGlanceReport>>(STORAGE_KEYS.AT_A_GLANCE_REPORTS, {}),
      savingsGoals: this.getItem<SavingsGoal[]>(STORAGE_KEYS.SAVINGS_GOALS, DEFAULT_SAVINGS_GOALS),
      recurringBills: this.getItem<RecurringBill[]>(STORAGE_KEYS.RECURRING_BILLS, DEFAULT_RECURRING_BILLS),
      yearlyGoals: this.getItem<YearlyGoal[]>(STORAGE_KEYS.YEARLY_GOALS, DEFAULT_YEARLY_GOALS),
      resources: this.getItem<ResourceLink[]>(STORAGE_KEYS.RESOURCES, DEFAULT_RESOURCES),
      monthlyExpenses: this.getItem('mylife_monthly_ledger_expenses', []),
      monthlyIncomes: this.getItem('mylife_monthly_ledger_incomes', []),
      monthlyDisbursements: this.getItem('mylife_monthly_ledger_disbursements', []),
      calendarEvents: this.getItem('mylife_calendar_custom_events', []),
      orgCategories: this.getItem('mylife_org_categories', []),
      personalCategories: this.getItem('mylife_personal_categories', []),
      businessCategories: this.getItem('mylife_business_categories', []),
      masterPin: this.getItem('mylife_notes_master_pin', null),
    };
  }

  static exportBackupJSON(): string {
    const data = this.loadAll();
    const payload = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      data,
    };
    return JSON.stringify(payload, null, 2);
  }

  static importBackupJSON(jsonStr: string): boolean {
    try {
      const parsed = JSON.parse(jsonStr);
      const data = parsed.data || parsed;
      if (data.user) this.setItem(STORAGE_KEYS.USER, data.user);
      if (data.financeFolders) this.setItem(STORAGE_KEYS.FINANCE_FOLDERS, data.financeFolders);
      if (data.accounts) this.setItem(STORAGE_KEYS.ACCOUNTS, data.accounts);
      if (data.categories) this.setItem(STORAGE_KEYS.CATEGORIES, data.categories);
      if (data.transactions) this.setItem(STORAGE_KEYS.TRANSACTIONS, data.transactions);
      if (data.budgets) this.setItem(STORAGE_KEYS.BUDGETS, data.budgets);
      if (data.debts) this.setItem(STORAGE_KEYS.DEBTS, data.debts);
      if (data.notes) this.setItem(STORAGE_KEYS.NOTES, data.notes);
      if (data.tasks) this.setItem(STORAGE_KEYS.TASKS, data.tasks);
      if (data.habits) this.setItem(STORAGE_KEYS.HABITS, data.habits);
      if (data.habitLogs) this.setItem(STORAGE_KEYS.HABIT_LOGS, data.habitLogs);
      if (data.personalReports) this.setItem(STORAGE_KEYS.PERSONAL_REPORTS, data.personalReports);
      if (data.monthlyPlans) this.setItem(STORAGE_KEYS.MONTHLY_PLANS, data.monthlyPlans);
      if (data.atAGlanceReports) this.setItem(STORAGE_KEYS.AT_A_GLANCE_REPORTS, data.atAGlanceReports);
      if (data.savingsGoals) this.setItem(STORAGE_KEYS.SAVINGS_GOALS, data.savingsGoals);
      if (data.recurringBills) this.setItem(STORAGE_KEYS.RECURRING_BILLS, data.recurringBills);
      if (data.yearlyGoals) this.setItem(STORAGE_KEYS.YEARLY_GOALS, data.yearlyGoals);
      if (data.resources) this.setItem(STORAGE_KEYS.RESOURCES, data.resources);
      if (data.monthlyExpenses) this.setItem('mylife_monthly_ledger_expenses', data.monthlyExpenses);
      if (data.monthlyIncomes) this.setItem('mylife_monthly_ledger_incomes', data.monthlyIncomes);
      if (data.monthlyDisbursements) this.setItem('mylife_monthly_ledger_disbursements', data.monthlyDisbursements);
      if (data.calendarEvents) this.setItem('mylife_calendar_custom_events', data.calendarEvents);
      if (data.orgCategories) this.setItem('mylife_org_categories', data.orgCategories);
      if (data.personalCategories) this.setItem('mylife_personal_categories', data.personalCategories);
      if (data.businessCategories) this.setItem('mylife_business_categories', data.businessCategories);
      if (data.masterPin) this.setItem('mylife_notes_master_pin', data.masterPin);
      return true;
    } catch (e) {
      console.error('Failed to import backup:', e);
      return false;
    }
  }

  static getMonthlyPlan(month: string): MonthlyPlan {
    const allPlans = this.getItem<Record<string, MonthlyPlan>>(STORAGE_KEYS.MONTHLY_PLANS, {});
    return allPlans[month] || { month };
  }

  static saveMonthlyPlan(plan: MonthlyPlan): void {
    const allPlans = this.getItem<Record<string, MonthlyPlan>>(STORAGE_KEYS.MONTHLY_PLANS, {});
    allPlans[plan.month] = plan;
    this.setItem(STORAGE_KEYS.MONTHLY_PLANS, allPlans);
  }

  static getAtAGlanceReport(month: string): AtAGlanceReport {
    const allReports = this.getItem<Record<string, AtAGlanceReport>>(STORAGE_KEYS.AT_A_GLANCE_REPORTS, {});
    return allReports[month] || { month };
  }

  static saveAtAGlanceReport(report: AtAGlanceReport): void {
    const allReports = this.getItem<Record<string, AtAGlanceReport>>(STORAGE_KEYS.AT_A_GLANCE_REPORTS, {});
    allReports[report.month] = report;
    this.setItem(STORAGE_KEYS.AT_A_GLANCE_REPORTS, allReports);
  }

  static exportPersonalReportCSV(entries: DailyReportEntry[], monthLabel: string): string {
    const headers = [
      'Date',
      'Quran_Tilawat_Ruku',
      'Quran_Tafseer_Ayat',
      'Hadith_Count',
      'Salah_Jamat',
      'Salah_Qaza',
      'Literature_General_Pages',
      'Literature_Syllabus_Pages',
      'Academic_Study_Hours',
      'Class_Count',
      'Class_Attended',
      'Contact_Member',
      'Contact_Associate',
      'Contact_Worker',
      'Contact_Supporter',
      'Contact_Friend',
      'Contact_Talented',
      'Contact_WellWisher',
      'Contact_Mukallif',
      'Dist_Literature',
      'Dist_Magazine',
      'Dist_StickerCard',
      'Dist_Gift',
      'Duty_Dawah_Hours',
      'Duty_Other_Hours',
      'Social_Media_Hours',
      'Newspaper_Read',
      'Exercise_Done',
      'Self_Accountability',
      'Sleep_Hours',
      'Remarks'
    ];

    const rows = entries.map(e => [
      e.date,
      e.quranTilawat ?? 0,
      e.quranTafseer ?? 0,
      e.hadithCount ?? 0,
      e.salahJamat ?? 0,
      e.salahQaza ?? 0,
      e.literatureGeneral ?? 0,
      e.literatureSyllabus ?? 0,
      e.academicStudyHours ?? 0,
      e.classCount ?? 0,
      e.classAttended ?? 0,
      e.contactMember ?? 0,
      e.contactAssociate ?? 0,
      e.contactWorker ?? 0,
      e.contactSupporter ?? 0,
      e.contactFriend ?? 0,
      e.contactTalented ?? 0,
      e.contactWellWisher ?? 0,
      e.contactMukallif ?? 0,
      e.distLiterature ?? 0,
      e.distMagazine ?? 0,
      e.distStickerCard ?? 0,
      e.distGift ?? 0,
      e.dutyDawahHours ?? 0,
      e.dutyOtherHours ?? 0,
      e.socialMediaHours ?? 0,
      e.newspaperRead ? 'Yes' : 'No',
      e.exerciseDone ? 'Yes' : 'No',
      e.selfAccountability ? 'Yes' : 'No',
      e.sleepHours ?? 0,
      `"${(e.remarks || '').replace(/"/g, '""')}"`
    ]);

    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  }

  static exportResourcesCSV(resources: ResourceLink[]): string {
    const headers = ['ID', 'Title', 'URL', 'Category', 'Description', 'Tags', 'IsPinned', 'IsFavorite', 'ClickCount', 'CreatedAt'];
    const rows = resources.map(r => [
      r.id,
      `"${(r.title || '').replace(/"/g, '""')}"`,
      `"${(r.url || '').replace(/"/g, '""')}"`,
      r.category,
      `"${(r.description || '').replace(/"/g, '""')}"`,
      `"${(r.tags || []).join(';')}"`,
      r.isPinned ? 'Yes' : 'No',
      r.isFavorite ? 'Yes' : 'No',
      r.clickCount ?? 0,
      r.createdAt
    ]);
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  static clearAll(): void {
    localStorage.clear();
  }
}
